// utils/secure-image-proxy.ts
/**
 * Utility functions for handling secure image access in private Supabase storage
 */

/**
 * The proxies (/api/secure-image, /api/watermark) only serve book images to a caller with access to the
 * book (owner, the creating browser's cookie, or the page's ?bt= / ?st= link token). An <img> request can't
 * carry the token on its own, so forward the current page's link tokens onto the proxy URL.
 */
export function withLinkAccess(proxyUrl: string): string {
  if (typeof window === 'undefined') return proxyUrl;
  try {
    const here = new URLSearchParams(window.location.search);
    const extra = new URLSearchParams();
    for (const k of ['bt', 'st']) {
      const v = here.get(k);
      if (v) extra.set(k, v);
    }
    const qs = extra.toString();
    return qs ? `${proxyUrl}${proxyUrl.includes('?') ? '&' : '?'}${qs}` : proxyUrl;
  } catch {
    return proxyUrl;
  }
}

/** A Supabase signed storage URL (object/sign/... with a token): the proxies serve it as-is. */
export function isSignedStorageUrl(url: string | null | undefined): boolean {
  if (!url || !isSupabaseStorageUrl(url)) return false;
  try {
    const u = new URL(url);
    return u.pathname.includes('/storage/v1/object/sign/') && !!u.searchParams.get('token');
  } catch {
    return false;
  }
}

/**
 * Which URL to hand /api/watermark for a page. The book API returns a signed imageUrl (plus the raw
 * originalImageUrl): the signed one works for any viewer of the page, including a signed-in owner whose
 * session lives only in localStorage (an <img> request carries no Bearer token, cookie-less devices have
 * no jb_books cookie). The raw URL is the fallback and still needs book access on the request.
 */
export function watermarkSourceUrl(page: { imageUrl?: string | null; originalImageUrl?: string | null }): string {
  if (isSignedStorageUrl(page.imageUrl)) return page.imageUrl as string;
  return page.originalImageUrl || page.imageUrl || '';
}

/**
 * Check if a URL is a Supabase storage URL
 */
export function isSupabaseStorageUrl(url: string): boolean {
  if (!url) return false;
  return url.includes('supabase.co/storage') || url.includes('.supabase.co/storage');
}

/**
 * Check if a URL needs to be proxied through our secure image endpoint
 */
export function needsSecureProxy(url: string): boolean {
  return isSupabaseStorageUrl(url);
}

/**
 * Convert a private Supabase storage URL to use our secure image proxy
 */
export function getSecureImageUrl(url: string): string {
  if (!needsSecureProxy(url)) {
    return url; // Return as-is for non-Supabase URLs
  }
  
  // Use our secure image proxy endpoint
  const params = new URLSearchParams({
    url: url
  });
  
  return withLinkAccess(`/api/secure-image?${params.toString()}`);
}

/**
 * Get image URL for watermarking - uses watermark API if needed
 */
export function getWatermarkedImageUrl(
  url: string, 
  watermarkText: string = 'Made by joonoo.art',
  position: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' = 'bottom-right',
  style: string = 'default'
): string {
  if (!url) return url;
  
  // For any URL that needs watermarking, use the watermark API
  const params = new URLSearchParams({
    url: url,
    text: watermarkText,
    position: position,
    style: style
  });
  
  return withLinkAccess(`/api/watermark?${params.toString()}`);
}

/**
 * Smart image URL getter - chooses the right approach based on URL type and requirements
 */
export function getImageUrl(
  url: string,
  options: {
    enableWatermark?: boolean;
    watermarkText?: string;
    watermarkPosition?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
    watermarkStyle?: string;
  } = {}
): string {
  if (!url) return url;
  
  const {
    enableWatermark = false,
    watermarkText = 'Made by joonoo.art',
    watermarkPosition = 'bottom-right',
    watermarkStyle = 'default'
  } = options;
  
  // If watermarking is enabled, use watermark API (it handles both secure and public URLs)
  if (enableWatermark) {
    return getWatermarkedImageUrl(url, watermarkText, watermarkPosition, watermarkStyle);
  }
  
  // If no watermark needed but URL is private Supabase, use secure proxy
  if (needsSecureProxy(url)) {
    return getSecureImageUrl(url);
  }
  
  // Return original URL for public URLs
  return url;
}

/**
 * React hook for getting secure image URLs
 */
export function useSecureImageUrl(
  url: string,
  options: {
    enableWatermark?: boolean;
    watermarkText?: string;
    watermarkPosition?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
    watermarkStyle?: string;
  } = {}
): string {
  return getImageUrl(url, options);
}
